// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One material's barcode rules, from the BOM page (`scanner/barcodeRules.ts`).
 *
 * The rules belong to the material code, not the line: every line of the material — in
 * this BOM and every other — shares them, which the dialog says by naming the lines. A
 * published BOM's rules can be changed too; they are not part of what publishing freezes.
 *
 * A rule is written from an example: the administrator scans or types a serial off a real
 * label and the rule is generated from it, then loosened or tightened by hand if need be.
 * It is saved only once it accepts its own example — the check that catches a hand edit
 * gone wrong — and a second box tries any other serial against it before it is.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import type { BarcodeRule } from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { IconButton } from "@/components/IconButton";
import { CheckIcon, CloseIcon, EditIcon, TrashIcon } from "@/components/icons";
import { Modal } from "@/components/Modal";
import { compileRule, PATTERN_MAX_LEN, patternFromExample } from "@/scanner/barcodeRules";
import { normalizeSerial } from "@/scanner/serials";

interface Draft {
  /** The rule being changed; null for a new one. */
  id: string | null;
  example: string;
  pattern: string;
  /** Typed into by hand: a new example no longer regenerates it. */
  patternEdited: boolean;
}

const NEW_DRAFT: Draft = { id: null, example: "", pattern: "", patternEdited: false };

export function BarcodeRulesDialog({
  materialCode,
  lines,
  rules,
  editable,
  onClose,
}: {
  materialCode: string;
  /** The position codes of this BOM's lines of the material. */
  lines: string[];
  rules: BarcodeRule[];
  editable: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Opens on the form when there is nothing to list yet.
  const [draft, setDraft] = useState<Draft | null>(
    editable && rules.length === 0 ? NEW_DRAFT : null,
  );
  const [trial, setTrial] = useState("");
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["barcode-rules"] });
  const save = useMutation({
    mutationFn: (rule: Draft) =>
      rule.id
        ? api.barcodeRules.update(rule.id, { pattern: rule.pattern, example: rule.example })
        : api.barcodeRules.create({
            material_code: materialCode,
            pattern: rule.pattern,
            example: rule.example,
          }),
    onSuccess: async () => {
      setDraft(null);
      setTrial("");
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.barcodeRules.remove(id),
    onSuccess: async () => {
      setPendingDelete(null);
      await refresh();
    },
  });

  const compiled = draft ? compileRule(draft.pattern) : null;
  const example = normalizeSerial(draft?.example);
  const acceptsExample = Boolean(compiled && example && compiled.test(example));
  const canSave = Boolean(draft && acceptsExample && !save.isPending);

  return (
    <Modal
      wide
      title={t("barcodeRules.title", { material: materialCode })}
      onClose={onClose}
      footer={
        <button type="button" onClick={onClose}>
          {t("common.close")}
        </button>
      }
    >
      <p className="hint">
        {t("barcodeRules.shared", { lines: lines.join("、"), n: lines.length })}
      </p>
      <p className="hint">{t("barcodeRules.anyMatches")}</p>

      {rules.length > 0 ? (
        <table className="barcode-rules">
          <thead>
            <tr>
              <th>{t("barcodeRules.pattern")}</th>
              <th>{t("barcodeRules.example")}</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td className="mono">{rule.pattern}</td>
                <td className="mono">{rule.example}</td>
                {editable && (
                  <td className="row-actions">
                    {pendingDelete === rule.id ? (
                      <>
                        <IconButton
                          label={t("common.confirm")}
                          icon={<CheckIcon />}
                          danger
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(rule.id)}
                        />
                        <IconButton
                          label={t("common.cancel")}
                          icon={<CloseIcon />}
                          onClick={() => setPendingDelete(null)}
                        />
                      </>
                    ) : (
                      <>
                        <IconButton
                          label={t("barcodeRules.edit", { pattern: rule.pattern })}
                          icon={<EditIcon />}
                          onClick={() =>
                            setDraft({
                              id: rule.id,
                              example: rule.example,
                              pattern: rule.pattern,
                              patternEdited: true,
                            })
                          }
                        />
                        <IconButton
                          label={t("barcodeRules.delete", { pattern: rule.pattern })}
                          icon={<TrashIcon />}
                          danger
                          onClick={() => setPendingDelete(rule.id)}
                        />
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">{t("barcodeRules.none")}</p>
      )}

      {editable && !draft && (
        <button type="button" onClick={() => setDraft(NEW_DRAFT)}>
          {t("barcodeRules.add")}
        </button>
      )}

      {draft && (
        <fieldset className="barcode-rule-form">
          <legend>{t(draft.id ? "barcodeRules.editTitle" : "barcodeRules.addTitle")}</legend>
          <div className="form-row">
            <label className="required" htmlFor="rule-example">
              {t("barcodeRules.example")}
            </label>
            <input
              id="rule-example"
              className="mono"
              autoComplete="off"
              placeholder={t("barcodeRules.examplePlaceholder")}
              value={draft.example}
              onChange={(event) => {
                const value = event.target.value;
                setDraft({
                  ...draft,
                  example: value,
                  pattern: draft.patternEdited ? draft.pattern : patternFromExample(value),
                });
              }}
            />
          </div>
          <div className="form-row">
            <label className="required" htmlFor="rule-pattern">
              {t("barcodeRules.pattern")}
            </label>
            <input
              id="rule-pattern"
              className="mono"
              autoComplete="off"
              maxLength={PATTERN_MAX_LEN}
              value={draft.pattern}
              onChange={(event) =>
                setDraft({ ...draft, pattern: event.target.value, patternEdited: true })
              }
            />
            {draft.patternEdited && draft.example.trim() && (
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    pattern: patternFromExample(draft.example),
                    patternEdited: false,
                  })
                }
              >
                {t("barcodeRules.regenerate")}
              </button>
            )}
          </div>
          {draft.pattern && (
            <p
              className={acceptsExample ? "hint" : "hint hint-error"}
              role={acceptsExample ? undefined : "alert"}
            >
              {!compiled
                ? t("barcodeRules.invalid")
                : acceptsExample
                  ? t("barcodeRules.acceptsExample")
                  : t("barcodeRules.rejectsExample")}
            </p>
          )}
          <p className="hint">{t("barcodeRules.generatedHint")}</p>
          <div className="form-row">
            <label htmlFor="rule-trial">{t("barcodeRules.trial")}</label>
            <input
              id="rule-trial"
              className="mono"
              autoComplete="off"
              value={trial}
              onChange={(event) => setTrial(event.target.value)}
            />
            {trial.trim() && compiled && (
              <span
                className={
                  compiled.test(normalizeSerial(trial))
                    ? "serial-note is-free"
                    : "serial-note is-installed"
                }
              >
                {t(
                  compiled.test(normalizeSerial(trial))
                    ? "barcodeRules.trialMatches"
                    : "barcodeRules.trialFails",
                )}
              </span>
            )}
          </div>
          <ErrorBanner error={save.error} />
          <div className="form-actions">
            <button
              type="button"
              className="button-primary"
              disabled={!canSave}
              onClick={() => draft && save.mutate(draft)}
            >
              {t("common.save")}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                save.reset();
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
        </fieldset>
      )}
      <ErrorBanner error={remove.error} />
    </Modal>
  );
}
